import clsx from 'clsx'
import React, { useEffect, useMemo, useState } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'

import { Text } from '@wartek-id/text'
import { Spinner } from '@wartek-id/spinner'

import PdfPreviewUploadIcon from '/assets/icons/pdf-preview-upload.svg'

import { useWindowDimensions } from 'utils/hooks/useWindowDimensions'
import { EmptyState, EmptyStateAction } from 'components/EmptyState'

// for dynamic import
// const workerSrc = async () => await import('utils/pdfWorkerEntry')

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`

const INITIAL_SCALE = 1.0
const PADDING_SIZE = 32
const PDF_OPTIONS = { isEvalSupported: false }

/**
 * @deprecated Bukti Karya is no longer served from guru-frontend and this is no longer maintained
 */
export const PDFPreviewer = ({
  data,
  onFilePreviewClick,
  pageLoaded = false,
  setFileIsLoading = null,
  isSticky = false,
  isRemoteUrl = false,
}) => {
  const [wrapperWidth, setWrapperWidth] = useState<number>(0)
  const { width, getElementDimensionById } = useWindowDimensions()

  const [numPages, setNumPages] = useState<number | null>(null)
  const [isLoaded, setIsLoaded] = useState(false)
  const file = useMemo(() => ({ data }), [data])
  const rootRef = React.useRef<HTMLDivElement>(null)

  const getFileToLoad = () => {
    if (isRemoteUrl) {
      return {
        // TODO: check cors if API already ready with true data
        url: file.data,
      }
    }
    return file
  }

  const onDocumentLoadSuccess = ({ numPages }) => {
    setIsLoaded(true)
    setNumPages(numPages)
    if (setFileIsLoading) setFileIsLoading(false)
  }

  useEffect(() => {
    const { width: elementWidth } = getElementDimensionById('viewerWrapper')
    setWrapperWidth(elementWidth)
  }, [getElementDimensionById])

  return (
    <>
      <div
        ref={rootRef}
        className="relative border border-gray-5 rounded-sm px-2 py-2 bg-gray-5"
      >
        {isLoaded && (
          <div
            className={clsx(
              'z-10',
              isSticky ? 'fixed top-[72px]' : 'absolute top-4'
            )}
            style={{
              right: isSticky
                ? (window.innerWidth - rootRef.current?.offsetWidth) / 2
                : 0,
            }}
          ></div>
        )}
        <div id="viewerContainer" className="relative z-0 overflow-auto">
          <div id="viewerWrapper">
            {file.data ? (
              <Document
                file={getFileToLoad()}
                options={PDF_OPTIONS}
                error={() => {
                  setFileIsLoading(false)

                  return (
                    <div className="w-full h-full flex items-center justify-center">
                      <Text variant="body" color="critical">
                        <EmptyState
                          title={'Terjadi Kesalahan dalam memuat dokumen'}
                          description={
                            'muat ulang halaman atau kembali ke halaman sebelumnya'
                          }
                        >
                          <EmptyStateAction
                            color="white"
                            onClick={() => history.back()}
                          >
                            Kembali Ke halaman sebelumnya
                          </EmptyStateAction>

                          <EmptyStateAction
                            color="black"
                            onClick={() => window.location.reload()}
                          >
                            Muat Ulang
                          </EmptyStateAction>
                        </EmptyState>
                      </Text>
                    </div>
                  )
                }}
                loading={() =>
                  !pageLoaded && (
                    <>
                      <Spinner size="lg" />
                      <Text as="span" className="pt-2" variant="body">
                        Harap Tunggu
                      </Text>
                    </>
                  )
                }
                externalLinkTarget="_blank"
                onLoadSuccess={onDocumentLoadSuccess}
              >
                {[...Array(numPages)].map((_, index) => (
                  <Page
                    key={`pdf-viewer-page-${index}@${INITIAL_SCALE}`}
                    pageNumber={index + 1}
                    className="pdf-viewer-page"
                    renderAnnotationLayer={false}
                    renderTextLayer={false}
                    scale={INITIAL_SCALE}
                    width={(wrapperWidth || width) - PADDING_SIZE}
                  />
                ))}
              </Document>
            ) : (
              <Text
                as="div"
                className="w-full h-screen flex flex-col place-content-center	place-items-center p-3 my-2 bg-surface-subdued border border-surface-neutral-subdued rounded"
                variant="body"
                color="informational"
                onClick={onFilePreviewClick}
              >
                <PdfPreviewUploadIcon className="mb-7" />
                <Text
                  as="span"
                  className="ml-1 w-[244px] text-center"
                  variant="body"
                >
                  Klik di sini untuk unggah dokumen dan melihat preview
                </Text>
              </Text>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default PDFPreviewer
